export const README_REFERRAL_COOKIE_NAME = "deepface_readme_referral";
export const README_REFERRAL_CAMPAIGN = "deepface_readme";
export const SOURCE_QUERY_REFERRAL_CAMPAIGN = "source_query";

type ReadmeReferralConfig = {
  source: string;
  campaign: string;
  linkName: string;
};

export type ReadmeReferralAttribution = {
  slug: string;
  source: string;
  campaign: string;
  linkName: string;
};

export type ReadmeReferralMetadata = {
  marketing_referral_source: string;
  marketing_referral_campaign: string;
  marketing_referral_link_name: string;
  marketing_referral_slug: string;
};

const README_REFERRAL_TARGETS: Record<string, ReadmeReferralConfig> = {
  "deepface-readme": {
    source: "github",
    campaign: README_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
  "deepface-readme-docs": {
    source: "github",
    campaign: README_REFERRAL_CAMPAIGN,
    linkName: "docs",
  },
  "indie-hackers": {
    source: "indie_hackers",
    campaign: SOURCE_QUERY_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
  "product-hunt": {
    source: "product_hunt",
    campaign: SOURCE_QUERY_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
  "hacker-news": {
    source: "hacker_news",
    campaign: SOURCE_QUERY_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
  reddit: {
    source: "reddit",
    campaign: SOURCE_QUERY_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
  linkedin: {
    source: "linkedin",
    campaign: SOURCE_QUERY_REFERRAL_CAMPAIGN,
    linkName: "site",
  },
};

const SOURCE_QUERY_REFERRAL_SLUGS: Record<string, string> = {
  deepface_repo: "deepface-readme",
  indie_hackers: "indie-hackers",
  product_hunt: "product-hunt",
  hacker_news: "hacker-news",
  reddit: "reddit",
  linkedin: "linkedin",
};

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeSourceQueryValue(value: string | null): string | null {
  const normalized = normalizeString(value)?.toLowerCase();
  if (!normalized) {
    return null;
  }

  const source = normalized.replaceAll("-", "_");
  return /^[a-z0-9_]{1,64}$/.test(source) ? source : null;
}

export function getReadmeReferralAttribution(
  slug: string,
): ReadmeReferralAttribution | null {
  if (slug.startsWith("utm:")) {
    const parts=slug.split(":");
    if(parts.length===5 && parts.slice(1).every(part=>/^[a-z0-9_-]{1,64}$/.test(part))) {
      return {slug,source:parts[1],campaign:parts[3],linkName:parts[4]};
    }
    return null;
  }
  const config = README_REFERRAL_TARGETS[slug];
  if (config) {
    return {
      slug,
      source: config.source,
      campaign: config.campaign,
      linkName: config.linkName,
    };
  }

  return null;
}

export function getReadmeReferralSlugForSourceQuery(
  source: string | null,
  options?: { preferDocs?: boolean },
) {
  const normalized = normalizeSourceQueryValue(source);
  if (!normalized) {
    return null;
  }

  if (normalized === "deepface_repo" && options?.preferDocs) {
    return "deepface-readme-docs";
  }

  return SOURCE_QUERY_REFERRAL_SLUGS[normalized] ?? null;
}

export function buildReadmeReferralMetadata(
  attribution: ReadmeReferralAttribution,
): ReadmeReferralMetadata {
  return {
    marketing_referral_source: attribution.source,
    marketing_referral_campaign: attribution.campaign,
    marketing_referral_link_name: attribution.linkName,
    marketing_referral_slug: attribution.slug,
  };
}

export function hasReadmeReferralMetadata(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  const slug = normalizeString(metadata?.marketing_referral_slug);
  const campaign = normalizeString(metadata?.marketing_referral_campaign);
  const source = normalizeString(metadata?.marketing_referral_source);
  const linkName = normalizeString(metadata?.marketing_referral_link_name);
  const attribution = slug ? getReadmeReferralAttribution(slug) : null;

  return (
    Boolean(attribution) &&
    campaign === attribution?.campaign &&
    source === attribution.source &&
    linkName === attribution.linkName
  );
}

export function mergeReadmeReferralMetadata<T extends Record<string, unknown>>(
  metadata: T,
  attribution: ReadmeReferralAttribution | null,
): T | (T & ReadmeReferralMetadata) {
  if (!attribution) {
    return metadata;
  }

  return {
    ...metadata,
    ...buildReadmeReferralMetadata(attribution),
  };
}

export function getReadmeReferralMetadataFromUserMetadata(
  metadata: unknown,
): ReadmeReferralMetadata | null {
  if (!isObjectRecord(metadata)) {
    return null;
  }

  const slug = normalizeString(metadata.marketing_referral_slug);
  const attribution = slug ? getReadmeReferralAttribution(slug) : null;
  if (!attribution) {
    return null;
  }

  return buildReadmeReferralMetadata(attribution);
}

export function readReadmeReferralAttributionFromCookieString(
  cookieString: string | null | undefined,
): ReadmeReferralAttribution | null {
  if (!cookieString) {
    return null;
  }

  const entries = cookieString.split(";");
  for (const entry of entries) {
    const [rawName, ...rawValueParts] = entry.trim().split("=");
    if (rawName !== README_REFERRAL_COOKIE_NAME) {
      continue;
    }

    const rawValue = rawValueParts.join("=");
    let decodedValue: string | null = null;
    try {
      decodedValue = rawValue ? decodeURIComponent(rawValue) : null;
    } catch {
      decodedValue = rawValue || null;
    }

    const value = normalizeString(decodedValue);
    return value ? getReadmeReferralAttribution(value) : null;
  }

  return null;
}

export const LAST_TOUCH_COOKIE_NAME = "deepface_last_referral";
function cleanCampaignLabel(value:string|null) {
 const label=value?.trim().toLowerCase();
 return label && /^[a-z0-9_-]{1,64}$/.test(label)?label:null;
}
export function attributionFromSearchParams(params:URLSearchParams) {
 const source=cleanCampaignLabel(params.get("utm_source"));
 if(source) {
  const medium=cleanCampaignLabel(params.get("utm_medium"))??"unspecified";
  const campaign=cleanCampaignLabel(params.get("utm_campaign"))??"unspecified";
  const content=cleanCampaignLabel(params.get("utm_content"))??"site";
  return getReadmeReferralAttribution(`utm:${source}:${medium}:${campaign}:${content}`);
 }
 const slug=getReadmeReferralSlugForSourceQuery(params.get("source"));
 return slug?getReadmeReferralAttribution(slug):null;
}
export function readLastTouchFromCookieString(cookie:string|null|undefined) {
 if(!cookie) return null;
 const entry=cookie.split(";").find(item=>item.trim().startsWith(`${LAST_TOUCH_COOKIE_NAME}=`));
 return entry?readReadmeReferralAttributionFromCookieString(entry.trim().replace(`${LAST_TOUCH_COOKIE_NAME}=`,`${README_REFERRAL_COOKIE_NAME}=`)):null;
}
